import authService from './authService';
import { ensureOk, readData, readList, readPaginated, toQuery } from './apiResponse';
import type { PaginatedList } from '../types/activity';
import type {
  ImportBatch,
  ImportConfirmInput,
  ImportFilter,
  ImportLayout,
  ImportLayoutInput,
  ImportLine,
  ImportLinePatch,
  ImportRule,
  ImportRuleInput,
  ImportSample,
  ImportTargetType,
} from '../types/finance';
import { IMPORT_MAX_BYTES, fileFormatFromName } from '../types/finance';
import { formatBytes } from '../utils/formatters';

/**
 * Validação local antes de enviar (o backend também valida): extensão e 5 MB. PDF vale para cartão
 * (fatura) e para conta (extrato do app do Itaú); `targetType` fica para futuras restrições por alvo.
 */
export const validateImportFile = (file: File, _targetType: ImportTargetType = 'account'): string | null => {
  const format = fileFormatFromName(file.name);
  if (!format) return 'Formato não aceito: envie um arquivo OFX, CSV, XLSX ou PDF.';
  if (file.size > IMPORT_MAX_BYTES) return `Arquivo maior que ${formatBytes(IMPORT_MAX_BYTES)} (${formatBytes(file.size)}).`;
  if (file.size === 0) return 'O arquivo está vazio.';
  return null;
};

const withOptions = (l: ImportLine): ImportLine => ({ ...l, reconcile_options: l?.reconcile_options ?? [] });

const withLines = (b: ImportBatch): ImportBatch => ({ ...b, lines: (b?.lines ?? []).map(withOptions) });

/**
 * Finanças → Importação de extratos: lotes (prévia → ajustes → confirmação), amostra para montar
 * layouts, layouts CSV/XLSX e regras de categoria. Multipart via FormData (o client omite o
 * Content-Type quando o corpo é FormData).
 */
class ImportService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  // ------------------------------------------------------------------ lotes

  /** POST /imports/sample (multipart `file`) — primeiras 15 linhas em colunas, para o editor de layout. */
  async sample(file: File): Promise<ImportSample> {
    const apiClient = await this.getApiClient();
    const form = new FormData();
    form.append('file', file, file.name);
    const response = await apiClient.post('/imports/sample', form);
    const s = await readData<ImportSample>(response, 'Falha ao ler a amostra do arquivo');
    return { ...s, rows: s?.rows ?? [] };
  }

  /**
   * POST /imports (multipart `file`, `target_type=account|card`, `target_id`, `import_layout_id?`, `password?`
   * para PDF protegido) → lote em prévia com `lines` (e `invoices` quando é fatura de cartão).
   */
  async create(file: File, targetType: ImportTargetType, targetId: number, layoutId?: number | null, password?: string | null): Promise<ImportBatch> {
    const apiClient = await this.getApiClient();
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('target_type', targetType);
    form.append('target_id', String(targetId));
    if (layoutId) form.append('import_layout_id', String(layoutId));
    if (password) form.append('password', password);
    const response = await apiClient.post('/imports', form);
    return withLines(await readData<ImportBatch>(response, 'Falha ao ler o arquivo de extrato'));
  }

  /** GET /imports?status=&page=&page_size= — histórico (sem linhas), mais recente primeiro. */
  async list(filter: ImportFilter = {}): Promise<PaginatedList<ImportBatch>> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/imports${toQuery({ status: filter.status || undefined, page: filter.page, page_size: filter.page_size })}`);
    return readPaginated<ImportBatch>(response, 'Falha ao listar as importações');
  }

  /** GET /imports/:id — com `lines`. */
  async getById(id: number): Promise<ImportBatch> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/imports/${id}`);
    return withLines(await readData<ImportBatch>(response, 'Falha ao carregar a importação'));
  }

  /**
   * PATCH /imports/:id/lines/:lineId { chosen_category_id?, status?, description?, target_invoice_id?,
   * reconcile_transaction_id? } — só em `previewing`. `reconcile_transaction_id` (lote de conta): id de uma
   * das `reconcile_options` ou 0 = não conciliar.
   */
  async patchLine(batchId: number, lineId: number, patch: ImportLinePatch): Promise<ImportLine> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/imports/${batchId}/lines/${lineId}`, patch);
    return withOptions(await readData<ImportLine>(response, 'Falha ao alterar a linha'));
  }

  /** POST /imports/:id/reapply-rules — re-sugere categoria nas linhas sem escolha manual. */
  async reapplyRules(batchId: number): Promise<ImportBatch> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/imports/${batchId}/reapply-rules`);
    return withLines(await readData<ImportBatch>(response, 'Falha ao reaplicar as regras'));
  }

  /**
   * POST /imports/:id/confirm { generate_future_installments?, update_balance? } — insere as linhas `new`
   * (409 se já confirmado). Em lote de conta vindo de PDF de extrato, `update_balance` (padrão true no
   * backend) grava o ajuste de saldo da conta com o saldo do extrato; a resposta traz `balance_updated`.
   */
  async confirm(batchId: number, options: ImportConfirmInput = {}): Promise<ImportBatch> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/imports/${batchId}/confirm`, options);
    return withLines(await readData<ImportBatch>(response, 'Falha ao confirmar a importação'));
  }

  /** POST /imports/:id/discard */
  async discard(batchId: number): Promise<ImportBatch> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/imports/${batchId}/discard`);
    return withLines(await readData<ImportBatch>(response, 'Falha ao descartar a importação'));
  }

  // ------------------------------------------------------------------ layouts

  /** GET /import-layouts */
  async getLayouts(): Promise<ImportLayout[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/import-layouts');
    return readList<ImportLayout>(response, 'Falha ao listar os layouts');
  }

  /** POST /import-layouts (409 nome repetido) */
  async createLayout(input: ImportLayoutInput): Promise<ImportLayout> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/import-layouts', input);
    return readData<ImportLayout>(response, 'Falha ao criar o layout');
  }

  /** PUT /import-layouts/:id */
  async updateLayout(id: number, input: ImportLayoutInput): Promise<ImportLayout> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/import-layouts/${id}`, input);
    return readData<ImportLayout>(response, 'Falha ao atualizar o layout');
  }

  /** DELETE /import-layouts/:id */
  async removeLayout(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/import-layouts/${id}`);
    await ensureOk(response, 'Falha ao excluir o layout');
  }

  // ------------------------------------------------------------------ regras

  /** GET /import-rules — ordem: prioridade, padrão. */
  async getRules(): Promise<ImportRule[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/import-rules');
    return readList<ImportRule>(response, 'Falha ao listar as regras de importação');
  }

  /** POST /import-rules */
  async createRule(input: ImportRuleInput): Promise<ImportRule> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/import-rules', input);
    return readData<ImportRule>(response, 'Falha ao criar a regra');
  }

  /** PUT /import-rules/:id */
  async updateRule(id: number, input: ImportRuleInput): Promise<ImportRule> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/import-rules/${id}`, input);
    return readData<ImportRule>(response, 'Falha ao atualizar a regra');
  }

  /** DELETE /import-rules/:id */
  async removeRule(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/import-rules/${id}`);
    await ensureOk(response, 'Falha ao excluir a regra');
  }
}

export const importService = new ImportService();
export default importService;
